import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

const FONT = {
  body: "'Soehne', sans-serif",
  emphasis: "'Soehne Kraftig', sans-serif",
  mono: "'Soehne Mono', monospace",
} as const

const tokens = {
  dark: {
    bg: 'bg.card',
    answerBg: 'bg.app',
    answerHoverBg: 'bg.card-elevated',
    border: 'border.subtle',
    focusBorder: 'duskBlue.500',
    accentBg: 'brand.primary',
    accentText: 'text.on-accent',
    shortcutBg: 'bg.input',
    shortcutText: 'text.secondary',
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
    textTertiary: 'text.tertiary',
    icon: 'icon.secondary',
  },
  light: {
    bg: 'light.bg.card-elevated',
    answerBg: 'light.bg.card',
    answerHoverBg: 'light.bg.segment-hover',
    border: 'light.border.subtle',
    focusBorder: 'light.border.input-focus',
    accentBg: 'light.brand.primary',
    accentText: 'light.text.on-accent',
    shortcutBg: 'light.bg.chip-secondary',
    shortcutText: 'light.text.chip-secondary',
    textPrimary: 'light.text.primary',
    textSecondary: 'light.text.secondary',
    textTertiary: 'light.text.tertiary',
    icon: 'light.icon.secondary',
  },
} as const

export const questionCardRootStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  w: 'full',
  /**
   * Preserve author-entered line breaks instead of collapsing them into a
   * single line, while still wrapping long lines.
   *
   * NOTE! Currently, QuestionCards are only rendered in [SteppedLists], which
   * makes this property redundant. However, we should still set it in the
   * event we ever want to render this elsewhere.
   * @see https://hivewatch.atlassian.net/browse/ENG-31260
   */
  whiteSpace: 'pre-wrap',
}

export const questionCardHelperTextStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  alignSelf: 'flex-end',
  color: tokens[colorScheme].textTertiary,
  fontFamily: FONT.body,
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '16px',
  m: 0,
})

export const questionCardStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: tokens[colorScheme].border,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  p: '20px',
  w: 'full',
})

export const questionCardQuestionStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].textPrimary,
  fontFamily: FONT.emphasis,
  fontSize: '18px',
  fontWeight: '500',
  lineHeight: '24px',
  m: 0,
})

export const questionCardSubtitleStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].textSecondary,
  fontFamily: FONT.body,
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px',
  m: 0,
  mt: '10px',
})

export const questionCardAnswerListStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  w: 'full',
}

export const questionCardAnswerOptionStyles = (
  isSelected: boolean,
  colorScheme: ColorScheme,
  isInteractive: boolean
): SystemStyleObject => ({
  alignItems: 'center',
  bg: isSelected ? tokens[colorScheme].answerBg : 'transparent',
  border: '1px solid',
  borderColor: isSelected ? tokens[colorScheme].focusBorder : 'transparent',
  color: tokens[colorScheme].textPrimary,
  cursor: isInteractive ? 'pointer' : 'default',
  display: 'flex',
  gap: '12px',
  minH: '44px',
  outline: 'none',
  px: '8px',
  py: '6px',
  transition:
    'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
  w: 'full',
  ...(isInteractive && {
    _focusVisible: {
      borderColor: tokens[colorScheme].focusBorder,
    },
    _hover: {
      bg: isSelected
        ? tokens[colorScheme].answerBg
        : tokens[colorScheme].answerHoverBg,
    },
  }),
})

export const questionCardShortcutStyles = (
  isSelected: boolean,
  colorScheme: ColorScheme
): SystemStyleObject => ({
  alignItems: 'center',
  bg: isSelected
    ? tokens[colorScheme].accentBg
    : tokens[colorScheme].shortcutBg,
  color: isSelected
    ? tokens[colorScheme].accentText
    : tokens[colorScheme].shortcutText,
  display: 'inline-flex',
  flexShrink: 0,
  fontFamily: FONT.mono,
  fontSize: '14px',
  fontWeight: '400',
  h: '28px',
  justifyContent: 'center',
  lineHeight: '20px',
  minW: '28px',
  px: '8px',
})

export const questionCardAnswerLabelStyles: SystemStyleObject = {
  color: 'inherit',
  fontFamily: FONT.emphasis,
  fontSize: '16px',
  fontWeight: '500',
  lineHeight: '22px',
  m: 0,
}

export const questionCardSubmitIconStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].icon,
  flexShrink: 0,
  h: '16px',
  ml: 'auto',
  w: '16px',
})
